import { zodResolver } from '@hookform/resolvers/zod';
import { useGetAllExamCoursesQuery } from '@report/graphql';
import { Box, Button, Divider, Flex, Form, FormDateInput, Text } from '@shared/components';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useQSetUsageReportSearchParams } from '../../hooks';
import { ExamCourseSearchSelection } from '../exam-course-search-selection';

import {
  QSetUsageReportFilterSectionSchema,
  type QSetUsageReportFilterSectionFormInput,
  type QSetUsageReportFilterSectionFormOutput,
} from './schema';

export default function QSetUsageReportFilterSection() {
  const formContext = useForm<
    QSetUsageReportFilterSectionFormInput,
    unknown,
    QSetUsageReportFilterSectionFormOutput
  >({
    resolver: zodResolver(QSetUsageReportFilterSectionSchema),
    defaultValues: {
      examStartDate: null,
      examEndDate: null,
      examCourse: null,
    },
  });

  const [{ examStartDate, examEndDate, examCourseId }, { changeFilters }] =
    useQSetUsageReportSearchParams();

  const onSubmit = (data: QSetUsageReportFilterSectionFormOutput) => {
    const { examStartDate, examEndDate, examCourse } = data;
    changeFilters({
      examStartDate,
      examEndDate,
      examCourseId: examCourse.value,
    });
  };

  const { data: examCourseData } = useGetAllExamCoursesQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const selectedExamCourse = examCourseData?.reportGetAllExamCourses?.examCourses.find((course) =>
      examCourseId?.includes(course.id),
    );
    formContext.reset({
      examStartDate: examStartDate,
      examEndDate: examEndDate,
      examCourse: selectedExamCourse
        ? {
            label: selectedExamCourse.code,
            value: selectedExamCourse.id,
          }
        : null,
    });
  }, [examStartDate, examEndDate, examCourseId, formContext, examCourseData]);

  const handleReset = () => {
    formContext.reset({
      examStartDate: null,
      examEndDate: null,
      examCourse: null,
    });
    changeFilters({ examStartDate: null, examEndDate: null, examCourseId: null });
  };

  const currentDate = new Date();

  return (
    <FormProvider {...formContext}>
      <Form
        sx={(theme) => ({
          p: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: theme.shape.rounded(2),
        })}
        onSubmit={formContext.handleSubmit(onSubmit)}
      >
        <Flex sx={(theme) => ({ gap: theme.spacing(3) })}>
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Date Filter:
          </Text>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              flex: 1,
              alignItems: 'flex-start',
            })}
          >
            <Box sx={{ flexBasis: '256px' }}>
              <FormDateInput
                label='Exam Start Date'
                name='examStartDate'
                inputFormat='DD MMM BBBB'
                labelFormat='MMMM BBBB'
                placeholder='DD-MM-YYYY'
                maxDate={currentDate}
                yearLabelFormat='BBBB'
                fullWidth
              />
            </Box>
            <Text sx={(theme) => ({ whiteSpace: 'nowrap', pt: theme.spacing(8) })}>to</Text>
            <Box sx={{ flexBasis: '256px' }}>
              <FormDateInput
                label='Exam End Date'
                name='examEndDate'
                inputFormat='DD MMM BBBB'
                labelFormat='MMMM BBBB'
                placeholder='DD-MM-YYYY'
                maxDate={currentDate}
                yearLabelFormat='BBBB'
                fullWidth
              />
            </Box>
          </Flex>
        </Flex>
        <Flex sx={(theme) => ({ gap: theme.spacing(3) })}>
          <Text variant='body1Semibold' sx={{ flexBasis: '160px' }}>
            Other Filters:
          </Text>
          <Box sx={{ flexBasis: '256px' }}>
            <ExamCourseSearchSelection name='examCourse' placeholder='เลือก Exam Course' />
          </Box>
        </Flex>
        <Divider />
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button variant='outlined' color='secondary' onClick={handleReset}>
            Reset
          </Button>
          <Button type='submit'>Apply</Button>
        </Flex>
      </Form>
    </FormProvider>
  );
}
